<template>
    <div class="pt-9 pb-12 lg:pb-36 bg-grey-400 bg-opacity-25">
        <div class="container">
            <div class="grid grid-cols-12 lg:gap-x-9">
                {{/* * */}}
                {{/* * */}}
                {{/* * */}}
                {{/* Steps bar */}}
                {{/* * */}}
                <div
                    class="col-span-12 lg:col-span-10 lg:col-start-2 mb-4.5 lg:mb-11 relative z-1 stepper"
                    :class="{
                        'stepper--first-step-is-done': currentStep === 1,
                        'stepper--second-step-is-done': currentStep === 2,
                    }"
                >
                    <div class="flex justify-between">
                        <div
                            v-for="(step, i) in steps"
                            :key="`step-${i}`"
                            class="w-[20%] text-center text-sm flex flex-col items-center leading-5 cursor-pointer"
                            @click="setCurrentStep(i)"
                        >
                            <span
                                class="font-Open-Sans-Bold min-h-[2.5rem] lg:min-h-0"
                                :class="i <= currentStep ? 'font-Open-Sans-Bold' : 'font-Open-Sans-Regular'"
                            >
                                {{ step.title }}
                            </span>
                            <div
                                class="w-4 h-4 max-w-[1rem] max-h-[1rem] rounded-full mt-2 transition step-bullet"
                                :class="i <= currentStep ? 'step-bullet--is-active' : 'bg-white'"
                            ></div>
                        </div>
                    </div>
                </div>

                {{/* * */}}
                {{/* * */}}
                {{/* * */}}
                {{/* Registration form (broken into 3 steps) - left side of container */}}
                {{/* * */}}
                <div class="col-span-12 lg:col-span-5">
                    <Transition name="fade" mode="out-in">
                        <h3 :key="stepTitle" class="section-title-extrabold mb-4.5 lg:mb-10">
                            {{ stepTitle }}
                        </h3>
                    </Transition>

                    <ValidationObserver v-slot="{ errors }" ref="form" slim>
                        <form
                            novalidate
                            class="lg:w-[calc(100%+2.25rem)] flex flex-col gap-y-6"
                            @submit.prevent="onSubmit"
                        >
                            {{/* * */}}
                            {{/* First step - student details */}}
                            {{/* * */}}
                            <Transition name="fade-in-step" mode="in-out">
                                <div
                                    v-show="currentStep === 0"
                                    class="lg:w-[calc(100%+2.25rem)] flex flex-col gap-y-4.5 lg:gap-y-6"
                                >
                                    <ValidationProvider vid="child_last_name" tag="div">
                                        <Input
                                            v-model="student_details.child_last_name"
                                            :label="$t('form.student-last-name')"
                                            :error="errors.child_last_name"
                                            :is-error-visible="isErrorVisible"
                                        />
                                    </ValidationProvider>

                                    <ValidationProvider vid="child_first_name" tag="div">
                                        <Input
                                            v-model="student_details.child_first_name"
                                            :label="$t('form.student-first-name')"
                                            :error="errors.child_first_name"
                                            :is-error-visible="isErrorVisible"
                                        />
                                    </ValidationProvider>

                                    <ValidationProvider vid="child_email" tag="div">
                                        <Input
                                            v-model="child_email"
                                            is-error-narrow
                                            :required="false"
                                            :label="$t('form.student-email')"
                                            :error="errors.child_email"
                                            :is-error-visible="isErrorVisible"
                                        />
                                    </ValidationProvider>

                                    <ValidationProvider vid="school_id" tag="div">
                                        <Input
                                            v-model="student_details.school_id"
                                            is-extended-dropdown
                                            :label="$t('form.school-name')"
                                            :error="errors.school_id"
                                            :options="schools"
                                            :is-error-visible="isErrorVisible"
                                        />
                                    </ValidationProvider>

                                    <div class="flex flex-col lg:flex-row lg:items-center gap-x-4.5">
                                        <span
                                            class="text-sm font-Source-Sans-Pro-Bold whitespace-nowrap w-3/12 pl-3 lg:pl-0 mb-3 lg:mb-0 text-left xl:text-right text-grey-900 uppercase input-label input-label--required"
                                        >
                                            <T t="form.grade-class" />
                                        </span>
                                        <div class="flex items-start gap-x-2 lg:gap-x-4 5 lg:pl-0">
                                            <div class="w-6/12 lg:w-4/12 min-w-[8.5rem]">
                                                <ValidationProvider vid="grade" tag="div">
                                                    <Input
                                                        v-model="student_details.grade"
                                                        is-dropdown
                                                        :error="errors.grade"
                                                        :options="grades"
                                                        :is-error-visible="isErrorVisible"
                                                    />
                                                </ValidationProvider>
                                            </div>
                                            <div class="w-6/12 lg:w-4/12 min-w-[8.5rem]">
                                                <ValidationProvider vid="class" tag="div">
                                                    <Input
                                                        v-model="student_details.class_"
                                                        :error="errors.class"
                                                        :is-error-visible="isErrorVisible"
                                                    />
                                                </ValidationProvider>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Transition>

                            {{/* * */}}
                            {{/* Second step - billing details */}}
                            {{/* * */}}
                            <Transition name="fade-in-step" mode="in-out">
                                <div
                                    v-show="currentStep === 1"
                                    class="lg:w-[calc(100%+2.25rem)] flex flex-col gap-y-4.5 lg:gap-y-6"
                                >
                                    <ValidationProvider vid="last_name" tag="div">
                                        <Input
                                            v-model="invoice_details.parent.last_name"
                                            narrow
                                            :label="$t('form.parent-last-name')"
                                            :error="errors.last_name"
                                            :is-error-visible="isErrorVisible"
                                        />

                                        <Transition name="error-fade">
                                            <div
                                                v-if="errors.child_first_name && errors.child_first_name.length"
                                                class="text-left mt-1 ml-auto pl-4.5 lg:pl-0 w-full lg:w-8/12 lg:mr-4"
                                            >
                                                <span class="text-error text-xs">{{ errors.child_first_name[0] }}</span>
                                            </div>
                                        </Transition>
                                    </ValidationProvider>

                                    <ValidationProvider vid="first_name" tag="div">
                                        <Input
                                            v-model="invoice_details.parent.first_name"
                                            narrow
                                            :label="$t('form.parent-first-name')"
                                            :error="errors.first_name"
                                            :is-error-visible="isErrorVisible"
                                        />
                                    </ValidationProvider>

                                    <div class="flex lg:items-center flex-col lg:flex-row gap-x-4.5">
                                        <span
                                            class="text-sm font-Source-Sans-Pro-Bold lg:w-3/12 lg:text-right mb-3 lg:mb-0 pl-3 lg:pl-0 text-grey-900 uppercase input-label input-label--required text-left flex lg:justify-end"
                                        >
                                            <T t="form.zip-code-city" />
                                        </span>
                                        <div class="lg:w-8/12 flex gap-x-4.5">
                                            <ValidationProvider vid="zip_code" tag="div" class="w-1/2">
                                                <Input
                                                    v-model="invoice_details.address.zip_code"
                                                    :error="errors.zip_code"
                                                    :is-error-visible="isErrorVisible"
                                                />
                                            </ValidationProvider>

                                            <ValidationProvider vid="city" tag="div" class="w-1/2">
                                                <Input
                                                    v-model="invoice_details.address.city"
                                                    :error="errors.city"
                                                    :is-error-visible="isErrorVisible"
                                                />
                                            </ValidationProvider>
                                        </div>
                                    </div>

                                    <ValidationProvider vid="address" tag="div">
                                        <Input
                                            v-model="invoice_details.address.street"
                                            narrow
                                            :label="$t('form.street')"
                                            :error="errors.address"
                                            :is-error-visible="isErrorVisible"
                                        />
                                    </ValidationProvider>

                                    <ValidationProvider vid="email" tag="div">
                                        <Input
                                            v-model="invoice_details.email"
                                            narrow
                                            :label="$t('form.email')"
                                            :error="errors.email"
                                            :is-error-visible="isErrorVisible"
                                        />
                                    </ValidationProvider>

                                    <ValidationProvider vid="phone" tag="div">
                                        <Input
                                            v-model="invoice_details.phone"
                                            narrow
                                            :label="$t('form.tel')"
                                            placeholder="+36 22 511 050"
                                            :error="errors.phone"
                                            :is-error-visible="isErrorVisible"
                                        />
                                    </ValidationProvider>

                                    <ValidationProvider vid="message" tag="div">
                                        <Input
                                            v-model="message"
                                            is-textarea
                                            narrow
                                            :required="false"
                                            :label="$t('form.message')"
                                        />
                                    </ValidationProvider>

                                    <div class="flex flex-col gap-y-4">
                                        <ValidationProvider vid="parental_approval" tag="div">
                                            <div class="p-field flex items-center lg:w-9/12 ml-auto pl-6">
                                                <div class="flex items-center">
                                                    <Checkbox
                                                        id="parental_approval_cbx"
                                                        v-model="invoice_details.parental_approval"
                                                        class="mt-1 w-auto"
                                                        :binary="true"
                                                    />
                                                    <label
                                                        for="parental_approval_cbx"
                                                        class="text-black text-sm font-Source-Sans-Pro-Regular"
                                                    >
                                                        <T t="form.parent-approval" />
                                                    </label>
                                                </div>
                                            </div>
                                            <InputError v-show="isErrorVisible" :errors="errors.parental_approval" />
                                        </ValidationProvider>

                                        <ValidationProvider vid="accept" tag="div">
                                            <div class="p-field flex items-start lg:w-9/12 ml-auto pl-6">
                                                <div class="flex flex-col">
                                                    <div class="flex items-center">
                                                        <Checkbox
                                                            id="privacy_policy_cbx"
                                                            v-model="invoice_details.accept"
                                                            class="mt-1"
                                                            :binary="true"
                                                        />
                                                        <label
                                                            for="privacy_policy_cbx"
                                                            class="text-black text-sm font-Source-Sans-Pro-Regular"
                                                            v-html="$t('form.terms-of-use')"
                                                        >
                                                        </label>
                                                    </div>
                                                    <InputError
                                                        v-show="isErrorVisible"
                                                        class="mt-2"
                                                        :errors="errors.accept"
                                                    />
                                                </div>
                                            </div>
                                        </ValidationProvider>
                                    </div>
                                </div>
                            </Transition>

                            {{/* * */}}
                            {{/* Third step - payment details */}}
                            {{/* * */}}
                            <Transition name="fade-in-step" mode="in-out">
                                <div
                                    v-show="currentStep === 2"
                                    class="lg:w-[calc(100%+2.25rem)] flex flex-col gap-y-4.5 lg:gap-y-6"
                                >
                                    <ValidationProvider vid="payment_method" tag="div">
                                        <fieldset class="flex flex-col gap-y-6">
                                            {{/* SimplePay */}}
                                            <SimplePayRadioInput
                                                :selected-payment-method="selected_payment_method"
                                                @update-payment-method="setPaymentMethod"
                                            />

                                            {{/* Bank transfer */}}
                                            <BankTransferRadioInput
                                                :selected-payment-method="selected_payment_method"
                                                @update-payment-method="setPaymentMethod"
                                            />
                                        </fieldset>

                                        <InputError
                                            v-show="isErrorVisible"
                                            class="mt-2"
                                            :errors="errors.payment_method"
                                        />
                                    </ValidationProvider>
                                </div>
                            </Transition>

                            {{/* * */}}
                            {{/* Submit button - visible at all steps */}}
                            {{/* * */}}
                            <div class="lg:w-[calc(100%+2.25rem)] lg:ml-3">
                                <div
                                    class="lg:ml-auto"
                                    :class="{ 'lg:w-9/12': currentStep === 0, 'lg:w-8/12': currentStep === 1 }"
                                >
                                    <div class="w-max mx-auto lg:mx-0" @click="onSubmit">
                                        <PButton
                                            :disabled="isButtonDisabled"
                                            type="button"
                                            :label="buttonLabel"
                                            blue
                                            :icon="`${isSubmittingPayment ? 'pi pi-spin pi-spinner' : null}`"
                                            lg
                                        />
                                    </div>
                                </div>
                            </div>
                        </form>
                    </ValidationObserver>
                </div>

                {{/* * */}}
                {{/* * */}}
                {{/* * */}}
                {{/* Info about the registration process - right side of container */}}
                {{/* * */}}
                <div class="col-span-12 lg:col-span-6 lg:col-start-7 mt-16 lg:mt-0">
                    {{/* Description */}}
                    <div class="mb-8 lg:mb-28">
                        <div
                            class="w-32 h-32 bg-contain bg-center bg-no-repeat"
                            :style="`background-image: url(${require('~/assets/images/logos/50-percent-discount.svg')})`"
                        ></div>

                        <h3 class="section-title mb-2 mt-4">
                            <T t="registration-process.title" />
                        </h3>

                        <h4 class="text-sm font-Open-Sans-Bold mb-7">
                            <T t="registration-process.subtitle" />
                        </h4>

                        <div class="font-Open-Sans-Regular" v-html="$t('registration-process.description')"></div>
                    </div>

                    {{/* Price */}}
                    <div>
                        <h5 class="uppercase font-Source-Sans-Pro-Bold text-sm mb-2 lg:mb-1">
                            <Transition name="fade" mode="out-in">
                                <div :key="$t('registration-process.price-title')">
                                    <T t="registration-process.price-title" />
                                </div>
                            </Transition>
                        </h5>

                        <h6 v-if="price" class="text-brand section-title">
                            {{ price }} / <span><T t="generic.school_year" /></span>
                        </h6>

                        <span v-if="$i18n.locale === 'hr' || kuna_price" class="mt-1 text-orange font-Exo-Bold text-lg">
                            {{ kuna_price }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SimplePayRadioInput from '~/components/UI/Inputs/SimplePayRadioInput.vue';
import BankTransferRadioInput from '~/components/UI/Inputs/BankTransferRadioInput.vue';

export default {
    name: 'RegistrationStepperSection',
    components: {
        BankTransferRadioInput,
        SimplePayRadioInput,
    },
    data() {
        return {
            isSubmitting: false,
            isSubmittingPayment: false,
            currentStep: 0,
            isErrorVisible: false,
            price: null,
            kuna_price: null,
            student_details: {
                child_first_name: null,
                child_last_name: null,
                school_id: null,
                grade: null,
                class_: null,
            },
            invoice_details: {
                parent: {
                    first_name: null,
                    last_name: null,
                },
                address: {
                    zip_code: null,
                    street: null,
                    city: null,
                },
                email: null,
                phone: null,
                parental_approval: false,
                accept: false,
            },
            message: null,
            child_email: null,
            selected_payment_method: null,
            schools: [],
            grades: [
                { id: '1', name: '1' },
                { id: '2', name: '2' },
                { id: '3', name: '3' },
                { id: '4', name: '4' },
                { id: '5', name: '5' },
                { id: '6', name: '6' },
                { id: '7', name: '7' },
                { id: '8', name: '8' },
                { id: '9', name: '9' },
                { id: '10', name: '10' },
                { id: '11', name: '11' },
                { id: '12', name: '12' },
                { id: '13', name: '13' },
                { id: '14', name: '14' },
                { id: '15', name: '15' },
            ],
            steps: [
                { title: this.$t('form.student-details') },
                { title: this.$t('form.invoice-details') },
                { title: this.$t('form.payment-method') },
            ],
        };
    },
    async fetch() {
        await this.$axios.$get('/school').then((res) => {
            this.schools = res.data;
        });
        await this.$axios
            .$get(`/config?school_id=${this.student_details.school_id}&grade=${this.student_details.grade}`)
            .then((res) => {
                this.price = res.data.brutto_price;
                this.kuna_price = res.data.brutto_kuna_price;
            });
    },
    computed: {
        isButtonDisabled() {
            if (this.isSubmitting) {
                return true;
            }
            if (this.currentStep === 0) {
                return !this.hasValues(this.student_details);
            }
            if (this.currentStep === 1) {
                return !this.hasValues(this.invoice_details);
            }
            if (this.currentStep === 2) {
                return !this.selected_payment_method;
            } else {
                return false;
            }
        },
        stepTitle() {
            if (this.currentStep === 1) {
                return this.$t('form.invoice-details');
            }
            if (this.currentStep === 2) {
                return this.$t('form.payment-method');
            } else {
                return this.$t('form.student-details');
            }
        },
        buttonLabel() {
            if (this.currentStep === 1) {
                return this.$t('form.choose-payment-method');
            }
            if (this.currentStep === 2) {
                return this.$t('form.proceed-to-payment');
            } else {
                return this.$t('form.invoice-details');
            }
        },
    },
    watch: {
        'student_details.school_id'(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.$fetch();
            }
        },
        'student_details.grade'(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.$fetch();
            }
        },
    },
    methods: {
        setPaymentMethod(value) {
            this.selected_payment_method = value;
        },
        getIsCurrentStepDone(filled, remaining) {
            return !Object.keys(filled).some((key) => Object.keys(remaining).includes(key));
        },
        hasValues(obj) {
            if (!obj) return false;
            return Object.keys(obj).every((key) => {
                const val = obj[key];
                return typeof val === 'object' ? this.hasValues(val) : val;
            });
        },
        scrollToTop() {
            window.scrollTo(0, 0);
        },
        setCurrentStep(step) {
            if (step === 0 && this.hasValues(this.student_details)) {
                this.currentStep = step;
            } else if (
                (step === 1 && this.hasValues(this.invoice_details)) ||
                (step === 0 && this.hasValues(this.invoice_details))
            ) {
                this.currentStep = step;
            }
            this.scrollToTop();
        },
        async onSubmit() {
            this.isSubmitting = true;

            try {
                await this.$axios
                    .$post('/order/create', {
                        payment_method: this.selected_payment_method,
                        first_name: this.invoice_details.parent.first_name,
                        last_name: this.invoice_details.parent.last_name,
                        email: this.invoice_details.email,
                        phone: this.invoice_details.phone,
                        city: this.invoice_details.address.city,
                        zip_code: this.invoice_details.address.zip_code,
                        address: this.invoice_details.address.street,
                        notes: this.message,
                        school_id: this.student_details.school_id,
                        grade: this.student_details.grade,
                        class: this.student_details.class_,
                        child_first_name: this.student_details.child_first_name,
                        child_last_name: this.student_details.child_last_name,
                        child_email: this.child_email,
                        accept: this.invoice_details.accept,
                    })
                    .then(async (res) => {
                        if (res.success === 0) {
                            this.isErrorVisible = true;

                            if (
                                this.currentStep === 0 &&
                                this.getIsCurrentStepDone(this.student_details, res.error) &&
                                !res.error.child_email
                            ) {
                                this.isErrorVisible = false;
                                this.currentStep = 1;
                            } else if (
                                this.currentStep === 1 &&
                                this.getIsCurrentStepDone(this.invoice_details, res.error) &&
                                !res.error.child_first_name
                            ) {
                                this.isErrorVisible = false;
                                this.currentStep = 2;
                            }
                            this.scrollToTop();
                        }
                        if (res.success === 1) {
                            this.isSubmittingPayment = true;

                            if (this.selected_payment_method === 3) {
                                await this.$axios
                                    // order/stripe/ for stripe alternative
                                    .$get(`/order/simplepay/${res.data.order_id}`)
                                    .then((r) => {
                                        window.location.href = r.data.url;
                                    })
                                    .catch((error) => {
                                        console.log(error);
                                    });
                            } else {
                                await this.$axios
                                    .$get(`/order/payment/${res.data.order_id}`)
                                    .then(() => {
                                        this.$store.commit('setCurrentOrderId', res.data.order_id);
                                        const routeName = 'registration-successful-successful_registration_id';
                                        const routeParams = { successful_registration_id: res.data.order_id };
                                        const routePath = this.localePath({ name: routeName, params: routeParams });
                                        this.$router.push(routePath);
                                    })
                                    .catch((error) => {
                                        console.log(error);
                                    });
                            }
                            this.isSubmittingPayment = false;
                            this.scrollToTop();
                        } else {
                            this.$refs.form.setErrors(res.error);
                        }
                    })
                    .catch((err) => {
                        console.log(err);
                    })
                    .finally(() => {
                        this.isSubmitting = false;
                        this.$fetch();
                    });
            } catch (err) {
                this.isSubmitting = false;
                console.log(err);
            }
        },
    },
};
</script>

<style scoped>
.stepper {
    &::before {
        content: '';
        @apply w-[80%] ml-[10%] h-[2px] bg-grey-300 block absolute bottom-[7px] z-[-1];
    }

    &::after {
        content: '';
        @apply w-0 ml-[10%] h-[2px] bg-black block absolute bottom-[7px] z-[-1] transition-all delay-[0.25s];
    }

    &--first-step-is-done {
        &::after {
            @apply w-[40%];
            transition-delay: 0s;
        }
    }

    &--second-step-is-done {
        &::after {
            @apply w-[80%];
            transition-delay: 0s;
        }
    }
}

.step-bullet {
    @apply relative overflow-hidden transition-all;
    border: 3px solid #c2c2c2;
    transition-delay: 0.25s;
    @media (min-width: 1024px) {
        @apply border-none;
        transition-delay: 0s;
        outline: 4px solid #c2c2c2;
        outline-offset: -4px;
    }

    &::before {
        content: '';
        @apply h-full bg-black transition-all z-20 block absolute left-0 top-0 w-full max-w-0;
    }

    &--is-active {
        @apply border-black;

        &::before {
            @apply w-full max-w-[1rem] delay-[0.25s];
        }
    }
}
</style>
